/**
 * Phase 3 Feature I — multi-pass chief complaint resolution (ranked CCs, ambiguity/compound flags).
 */

import { FEATURE_STATUS } from './ddxTemporalTaxonomy.js';
import { temporalFitScore } from './ddxKbProjection.js';
import { matchAllChiefComplaintIdsFromText } from './ddxChiefComplaintRules.js';
import { stripJsonFences } from './ddxTemporalExtractor.js';

/**
 * @typedef {Object} ResolvedCC
 * @property {string} ccId
 * @property {string} label
 * @property {number} confidence
 * @property {string} resolutionPath
 */

/**
 * @typedef {Object} ChiefComplaintResolution
 * @property {ResolvedCC[]} candidates
 * @property {boolean} isAmbiguous
 * @property {boolean} isCompound
 * @property {string[]} warnings
 */

/**
 * @typedef {Object} ChiefComplaintResolverDeps
 * @property {(ccId: string) => Promise<object | null>} [getChiefComplaint]
 * @property {(ccData: object) => Promise<object[]>} [getEdgesForChiefComplaint]
 * @property {(transcriptText: string) => Promise<ResolvedCC[]>} [semanticMatchCc]
 * @property {(transcriptText: string, extraction: import('./ddxTypes.js').TemporalExtractionResult) => Promise<ResolvedCC[]>} [callChiefComplaintLlm]
 */

const PASS1_CONF = 0.92;
const SEMANTIC_MIN = 0.68;
const SEMANTIC_MAX = 0.88;
const LLM_CONF = 0.72;
const NEED_LLM_BELOW = 0.65;
const HIGH_CONF = 0.7;
const AMBIGUOUS_GAP = 0.15;
const COMPOUND_OVERLAP_MAX = 0.4;
const MAX_CANDIDATES = 5;

/**
 * @param {string[]} idsA
 * @param {string[]} idsB
 * @returns {number} Jaccard similarity
 */
function jaccard(idsA, idsB) {
  const a = new Set(idsA);
  const b = new Set(idsB);
  if (a.size === 0 && b.size === 0) return 1;
  let inter = 0;
  for (const x of a) {
    if (b.has(x)) inter += 1;
  }
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

/**
 * @param {ResolvedCC[]} ranked
 * @param {Map<string, string[]>} priorityByCc
 * @returns {boolean}
 */
function computeIsCompound(ranked, priorityByCc) {
  const high = ranked.filter((c) => c.confidence > HIGH_CONF);
  if (high.length < 2) return false;
  for (let i = 0; i < high.length; i += 1) {
    for (let j = i + 1; j < high.length; j += 1) {
      const pa = priorityByCc.get(high[i].ccId) || [];
      const pb = priorityByCc.get(high[j].ccId) || [];
      if (pa.length === 0 || pb.length === 0) continue;
      if (jaccard(pa, pb) < COMPOUND_OVERLAP_MAX) return true;
    }
  }
  return false;
}

/**
 * Parse managed query `ddx-chief-complaint-resolution` JSON.
 * @param {unknown} raw
 * @returns {ResolvedCC[]}
 */
export function parseChiefComplaintLlmResponse(raw) {
  let obj;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(stripJsonFences(raw));
    } catch {
      return [];
    }
  } else if (raw && typeof raw === 'object') {
    obj = raw;
  } else {
    return [];
  }
  const arr = Array.isArray(obj.candidates) ? obj.candidates : [];
  /** @type {ResolvedCC[]} */
  const out = [];
  for (const c of arr) {
    if (!c || typeof c !== 'object') continue;
    const ccId = c.ccId != null ? String(c.ccId).trim() : '';
    if (!ccId) continue;
    const conf = typeof c.confidence === 'number' && !Number.isNaN(c.confidence) ? c.confidence : LLM_CONF;
    out.push({
      ccId,
      label: c.label != null ? String(c.label) : ccId,
      confidence: Math.max(0, Math.min(1, conf)),
      resolutionPath: c.resolutionPath != null ? String(c.resolutionPath) : 'llm',
    });
  }
  return out;
}

/**
 * Merge candidates by ccId keeping max confidence and preferring richer resolutionPath order.
 * @param {ResolvedCC[]} list
 * @returns {ResolvedCC[]}
 */
function mergeByCcId(list) {
  /** @type {Map<string, ResolvedCC>} */
  const m = new Map();
  for (const c of list) {
    const prev = m.get(c.ccId);
    if (!prev || c.confidence > prev.confidence) m.set(c.ccId, { ...c });
  }
  return [...m.values()];
}

/**
 * @param {string} transcriptText
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {ChiefComplaintResolverDeps} [deps]
 * @returns {Promise<ChiefComplaintResolution>}
 */
export async function resolveChiefComplaint(transcriptText, extraction, deps = {}) {
  const warnings = [];
  const text = String(transcriptText || '').trim();
  /** @type {ResolvedCC[]} */
  let candidates = [];

  const pass1Ids = matchAllChiefComplaintIdsFromText(text);
  for (const id of pass1Ids) {
    candidates.push({
      ccId: id,
      label: id,
      confidence: PASS1_CONF,
      resolutionPath: 'exact',
    });
  }
  candidates = mergeByCcId(candidates);

  if (typeof deps.semanticMatchCc === 'function') {
    try {
      const sem = await deps.semanticMatchCc(text);
      for (const c of sem || []) {
        if (!c?.ccId) continue;
        const conf = Math.max(SEMANTIC_MIN, Math.min(SEMANTIC_MAX, c.confidence ?? 0.75));
        candidates.push({ ...c, confidence: conf, resolutionPath: c.resolutionPath || 'semantic' });
      }
      candidates = mergeByCcId(candidates);
    } catch (e) {
      warnings.push(`semantic_match_failed:${(e && e.message) || e}`);
    }
  }

  const acuity = extraction?.acuityEstimate || 'indeterminate';
  if (typeof deps.getChiefComplaint === 'function') {
    const temporalAdjusted = [];
    for (const c of candidates) {
      let conf = c.confidence;
      try {
        const cc = await deps.getChiefComplaint(c.ccId);
        const buckets = Array.isArray(cc?.temporalTriageBuckets) ? cc.temporalTriageBuckets : [];
        if (buckets.length > 0) {
          const fit = temporalFitScore(acuity, buckets);
          conf = Math.min(0.98, conf * (0.85 + 0.15 * fit));
        }
      } catch {
        /* ignore */
      }
      temporalAdjusted.push({ ...c, confidence: conf });
    }
    candidates = mergeByCcId(temporalAdjusted);
  }

  if (typeof deps.getChiefComplaint === 'function' && typeof deps.getEdgesForChiefComplaint === 'function') {
    const present = new Set();
    for (const kf of extraction?.keyFeatures || []) {
      if (kf.featureId && kf.featureStatus === FEATURE_STATUS.PRESENT) present.add(kf.featureId);
    }
    if (present.size > 0) {
      const boosted = [];
      for (const c of candidates) {
        let bonus = 0;
        try {
          const cc = await deps.getChiefComplaint(c.ccId);
          if (!cc) {
            boosted.push(c);
            continue;
          }
          const edges = await deps.getEdgesForChiefComplaint(cc);
          for (const e of edges || []) {
            if (!e?.featureId || !present.has(e.featureId)) continue;
            if (e.direction === 'contradicts') continue;
            const s = typeof e.edgeStrength === 'number' ? e.edgeStrength : 0;
            bonus += Math.min(0.08, s * 0.06);
          }
        } catch {
          /* ignore */
        }
        boosted.push({
          ...c,
          confidence: Math.min(0.99, c.confidence + bonus),
          resolutionPath: bonus > 0.02 ? 'feature_inferred' : c.resolutionPath,
        });
      }
      candidates = mergeByCcId(boosted);
    }
  }

  candidates.sort((a, b) => b.confidence - a.confidence);
  const topConf = candidates[0]?.confidence ?? 0;
  const needLlm = candidates.length === 0 || topConf < NEED_LLM_BELOW;
  if (needLlm && typeof deps.callChiefComplaintLlm === 'function') {
    try {
      const llmCands = await deps.callChiefComplaintLlm(text, extraction);
      for (const c of llmCands || []) {
        if (!c?.ccId) continue;
        candidates.push({
          ...c,
          confidence: c.confidence ?? LLM_CONF,
          resolutionPath: c.resolutionPath || 'llm',
        });
      }
      candidates = mergeByCcId(candidates);
      candidates.sort((a, b) => b.confidence - a.confidence);
    } catch (e) {
      warnings.push(`llm_cc_resolution_failed:${(e && e.message) || e}`);
    }
  }

  candidates = candidates.slice(0, MAX_CANDIDATES);

  /** @type {Map<string, string[]>} */
  const priorityByCc = new Map();
  if (typeof deps.getChiefComplaint === 'function') {
    for (const c of candidates) {
      try {
        const cc = await deps.getChiefComplaint(c.ccId);
        const p = Array.isArray(cc?.priorityDxIds) ? cc.priorityDxIds.map(String) : [];
        priorityByCc.set(c.ccId, p);
        if (c.label === c.ccId && cc?.chiefComplaint) {
          c.label = String(cc.chiefComplaint);
        }
      } catch {
        priorityByCc.set(c.ccId, []);
      }
    }
  }

  const isAmbiguous =
    candidates.length >= 2 &&
    Math.abs(candidates[0].confidence - candidates[1].confidence) <= AMBIGUOUS_GAP;

  const isCompound = computeIsCompound(candidates, priorityByCc);

  return {
    candidates,
    isAmbiguous,
    isCompound,
    warnings,
  };
}

/**
 * CC ids to load for KB projection (multi-CC when ambiguous/compound and multiple high-confidence).
 * @param {ChiefComplaintResolution} resolution
 * @returns {string[]}
 */
export function selectChiefComplaintIdsForProjection(resolution) {
  const { candidates, isAmbiguous, isCompound } = resolution;
  const threshold = 0.65;
  const high = candidates.filter((c) => c.confidence > threshold);
  if (isAmbiguous || isCompound) {
    return high.map((c) => c.ccId);
  }
  if (candidates.length === 0) return [];
  return [candidates[0].ccId];
}
